import { DevelocityLogoColor, GradleBuildTool } from '@/components/Logos';
import {
  CubeTransparentIcon,
  CpuChipIcon,
  BookOpenIcon,
  ChartBarIcon,
  AcademicCapIcon,
  CheckBadgeIcon,
  NewspaperIcon,
  SwatchIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  DocumentIcon,
  EnvelopeIcon,
  ComputerDesktopIcon,
  PlayCircleIcon,
  CogIcon,
  CodeBracketSquareIcon,
  Square3Stack3DIcon,
  CloudArrowUpIcon,
  CommandLineIcon,
  UserGroupIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline';
import {
  SpringBootShortLogo,
  ApacheSoftwareFoundationShortLogo,
  JUnitShort,
  KotlinLogo,
  TestcontainersLogo,
} from '@/components/Logos';
import { AISquareIcon, BoxCycleIcon, AccelerateIcon, TroubleshootIcon, ObserveIcon } from '@/components/Icons';
import dpeu from './dpeu.jpg';
import eventswebinars from './eventswebinars.jpg';
import { StaticImageData } from 'next/image';
import React from 'react';

export type SubMenuKeyType =
  | 'product'
  | 'use-cases'
  | 'solutions-build-systems'
  | 'integrations'
  | 'resources'
  | 'oss-projects'
  | 'learning-center'
  | 'about-us'
  | 'brand';

export type SubMenuItemType = {
  icon?: typeof CubeTransparentIcon;
  buttonContent: string;
  menuItemKey?: SubMenuKeyType;
  logo?: React.ReactElement;
  link?: { href: string; external: boolean };
};

type NavigationBarItemsType = {
  leftNavSection: {
    title: string;
    subMenuItems: SubMenuItemType[];
  };
  rightNavSectionTitle?: string;
  href?: string;
}[];

export const navigationBarItems: NavigationBarItemsType = [
  {
    leftNavSection: {
      title: 'Products',
      subMenuItems: [
        {
          buttonContent: 'Develocity',
          menuItemKey: 'product',
          logo: <DevelocityLogoColor className="h-4 text-white" />,
          link: { href: '/develocity/', external: false },
        },
        {
          buttonContent: 'Build Tool',
          logo: <GradleBuildTool className="h-9 w-36" />,
          link: { href: 'https://gradle.org/', external: true },
        },
      ],
    },

    rightNavSectionTitle: 'Features',
  },
  {
    leftNavSection: {
      title: 'Solutions',
      subMenuItems: [
        {
          icon: UserGroupIcon,
          buttonContent: 'Use Cases',
          menuItemKey: 'use-cases',
        },
        {
          icon: CubeTransparentIcon,
          buttonContent: 'Build Systems',
          menuItemKey: 'solutions-build-systems',
        },
        {
          icon: CpuChipIcon,
          buttonContent: 'Integrations',
          menuItemKey: 'integrations',
        },
      ],
    },
  },
  {
    leftNavSection: {
      title: 'Resources',
      subMenuItems: [
        {
          icon: AcademicCapIcon,
          buttonContent: 'Learning Center',
          menuItemKey: 'learning-center',
        },
        {
          icon: BookOpenIcon,
          buttonContent: 'Resources',
          menuItemKey: 'resources',
        },
        {
          icon: ChartBarIcon,
          buttonContent: 'OSS Projects',
          menuItemKey: 'oss-projects',
        },
      ],
    },
  },
  {
    leftNavSection: { title: 'Pricing', subMenuItems: [] },
    href: '/pricing',
  },
  {
    leftNavSection: { title: 'Customers', subMenuItems: [] },
    href: '/customers',
  },
  {
    leftNavSection: {
      title: 'Company',
      subMenuItems: [
        {
          icon: CheckBadgeIcon,
          buttonContent: 'About Us',
          menuItemKey: 'about-us',
        },
        {
          icon: NewspaperIcon,
          buttonContent: 'Brand',
          menuItemKey: 'brand',
        },
        {
          icon: SwatchIcon,
          buttonContent: 'Press & Media',
          link: { href: '/press-media/', external: false },
        },
      ],
    },
  },
];

type SubMenuColumnType = 'links' | 'cards' | 'mini-cards';

type SubMenuValueType = {
  type: SubMenuColumnType;
  title?: string;
  link?: {
    href: string;
    external?: boolean;
  };
  tab?: string;
  icon?: typeof CubeTransparentIcon | typeof AccelerateIcon;
  items: {
    title: string;
    icon?: typeof CubeTransparentIcon;
    reactIcon?: React.ReactElement;
    text?: string;
    bgImg?: StaticImageData;
    link: {
      text?: string;
      href: string;
      external?: boolean;
    };
  }[];
}[];

type SubMenuItemsType = Record<SubMenuKeyType, SubMenuValueType>;

export const subMenuItems: SubMenuItemsType = {
  product: [
    {
      type: 'links',
      title: 'Observe',
      link: { href: '/develocity/observe/', external: false },
      icon: ObserveIcon,
      items: [
        {
          title: 'Develocity 360',
          link: { href: '/develocity/product/develocity-360/', external: false },
        },
        {
          title: 'Analytics',
          link: { href: '/develocity/product/analytics/', external: false },
        },
        {
          title: 'Performance Insights',
          link: { href: '/develocity/product/performance-insights/', external: false },
        },
      ],
    },
    {
      type: 'links',
      title: 'Accelerate',
      link: { href: '/develocity/accelerate/', external: false },
      icon: AccelerateIcon,
      items: [
        {
          title: 'Build Cache',
          link: { href: '/develocity/product/build-cache/', external: false },
        },
        {
          title: 'Predictive Test Selection',
          link: { href: '/develocity/product/predictive-test-selection/', external: false },
        },
        {
          title: 'Test Distribution',
          link: { href: '/develocity/product/test-distribution/', external: false },
        },
      ],
    },
    {
      type: 'links',
      title: 'Troubleshoot',
      link: { href: '/develocity/troubleshoot/', external: false },
      icon: TroubleshootIcon,
      items: [
        {
          title: 'Build Scan',
          link: { href: '/develocity/product/build-scan/', external: false },
        },
        {
          title: 'Flaky Test Detection',
          link: { href: '/develocity/product/flaky-test-detection/', external: false },
        },
        {
          title: 'Failure Analytics',
          link: { href: '/develocity/product/failure-analytics/', external: false },
        },
      ],
    },

    {
      type: 'links',
      title: 'Develocity',
      link: { href: '/develocity/', external: false },
      items: [
        {
          title: 'Overview',
          link: { href: '/develocity/', external: false },
        },
        {
          title: 'Security',
          link: { href: '/develocity/solutions/security/', external: false },
        },
      ],
    },
  ],
  'use-cases': [
    {
      type: 'links',
      items: [
        {
          title: 'Generative AI',
          reactIcon: <AISquareIcon monochrome className="size-4 group-hover:text-light-blue" />,
          link: { href: '/solutions/generative-ai/', external: false },
        },
        {
          title: 'DORA & Continuous Delivery',
          reactIcon: <BoxCycleIcon monochrome className="size-4 group-hover:text-light-blue" />,
          link: { href: '/solutions/dora-and-continuous-delivery/', external: false },
        },
      ],
    },
  ],
  'solutions-build-systems': [
    {
      type: 'links',
      items: [
        {
          title: 'Gradle Build Tool',
          link: { href: '/develocity/solutions/gradle-build-tool/', external: false },
        },
        {
          title: 'Android',
          link: { href: '/develocity/solutions/android/', external: false },
        },
        {
          title: 'Apache Maven',
          link: { href: '/develocity/solutions/maven/', external: false },
        },
        {
          title: 'sbt',
          link: { href: '/develocity/solutions/sbt/', external: false },
        },
      ],
    },
  ],
  integrations: [
    {
      type: 'links',
      items: [
        {
          title: 'CI Systems',
          icon: CogIcon,
          link: { href: '/develocity/integrations#ci-systems', external: false },
        },
        {
          title: 'Programming Languages',
          icon: CodeBracketSquareIcon,
          link: { href: '/develocity/integrations#programming-languages', external: false },
        },
        {
          title: 'IDEs',
          icon: CpuChipIcon,
          link: { href: '/develocity/integrations#ides', external: false },
        },
        {
          title: 'Test Frameworks',
          icon: Square3Stack3DIcon,
          link: { href: '/develocity/integrations#test-frameworks', external: false },
        },
      ],
    },
    {
      type: 'links',
      items: [
        {
          title: 'Cloud Infrastructure',
          icon: CloudArrowUpIcon,
          link: { href: '/develocity/integrations#cloud-infrastructure', external: false },
        },
        {
          title: 'API & Data Reporting',
          icon: CommandLineIcon,
          link: { href: '/develocity/integrations#enterprise-rest-api', external: false },
        },
        {
          title: 'Build Systems & Package Managers',
          icon: QrCodeIcon,
          link: { href: '/develocity/integrations#build-systems', external: false },
        },
      ],
    },
  ],
  resources: [
    {
      type: 'links',
      items: [
        {
          title: 'Release Notes',
          icon: ClipboardDocumentListIcon,
          link: { href: '/develocity/releases/', external: false },
        },
        {
          title: 'Develocity Documentation',
          icon: DocumentTextIcon,
          link: { href: '/develocity/resources/', external: false },
        },
        {
          title: 'API Documentation',
          icon: DocumentIcon,
          link: { href: 'https://docs.gradle.com/develocity/api-manual/', external: false },
        },
        {
          title: 'Ebooks & Whitepapers',
          icon: BookOpenIcon,
          link: { href: '/resources/ebooks-and-whitepapers/', external: false },
        },
      ],
    },
    {
      type: 'links',
      items: [
        {
          title: 'DPE Newsletter',
          icon: EnvelopeIcon,
          link: { href: '/gradle-dpe-newsletters/', external: false },
        },
        {
          title: 'Blog',
          icon: ComputerDesktopIcon,
          link: { href: '/blog/', external: false },
        },
        {
          title: 'YouTube',
          icon: PlayCircleIcon,
          link: { href: 'https://www.youtube.com/channel/UCvClhveoEjokKIuBAsSjEwQ', external: true },
        },
      ],
    },
  ],
  'oss-projects': [
    {
      type: 'mini-cards',
      items: [
        {
          title: 'Spring',
          reactIcon: (
            <SpringBootShortLogo className="absolute right-4 size-14 opacity-50 grayscale group-hover:opacity-100" />
          ),
          link: {
            href: '/oss-sponsored-by-develocity/#spring',
            external: true,
          },
        },
        {
          title: 'Apache Software Foundation',
          reactIcon: (
            <ApacheSoftwareFoundationShortLogo className="absolute right-4 !size-14 opacity-50 grayscale group-hover:opacity-100" />
          ),
          link: { href: '/oss-sponsored-by-develocity/#the-apache-software-foundation', external: true },
        },
        {
          title: 'JUnit',
          reactIcon: <JUnitShort className="absolute right-4 size-14 opacity-50 grayscale group-hover:opacity-100" />,
          link: {
            href: '/oss-sponsored-by-develocity/#junit',
            external: true,
          },
        },
        {
          title: 'Kotlin',
          reactIcon: <KotlinLogo className="absolute right-4 size-14 opacity-50 grayscale group-hover:opacity-100" />,
          link: { href: '/oss-sponsored-by-develocity/#the-kotlin-foundation', external: true },
        },

        {
          title: 'Testcontainers',
          reactIcon: (
            <TestcontainersLogo className="absolute right-4 size-14 opacity-50 grayscale group-hover:opacity-100" />
          ),
          link: { href: '/oss-sponsored-by-develocity/#testcontainers', external: true },
        },
        {
          title: 'See all',
          link: { href: '/oss-sponsored-by-develocity', external: false },
        },
      ],
    },
  ],
  'learning-center': [
    {
      type: 'cards',
      items: [
        {
          title: 'DPE University',
          text: 'Start your journey to Developer Productivity Engineering mastery.',
          bgImg: dpeu,
          link: { href: 'https://dpeuniversity.gradle.com/app/', external: false, text: 'Get started' },
        },
        {
          title: 'Events & Webinars',
          text: 'Learn from experts and connect with the community through our in-person and virtual events.',
          bgImg: eventswebinars,
          link: { href: '/events/', external: false, text: 'Learn more' },
        },
      ],
    },
  ],
  'about-us': [
    {
      type: 'links',
      items: [
        {
          title: 'About us',
          link: { href: '/about-us/', external: false },
        },
        {
          title: 'Our story',
          link: { href: '/about-us/#our-story', external: false },
        },
        {
          title: 'Awards',
          link: { href: '/about-us/#awards', external: false },
        },
        {
          title: 'Our team',
          link: { href: '/about-us/#our-team', external: false },
        },
        {
          title: 'Careers',
          link: {
            href: '/careers/',
            external: false,
          },
        },
      ],
    },
  ],
  brand: [
    {
      type: 'links',
      items: [
        {
          title: 'Assets & Guidelines',
          link: { href: '/brand/', external: false },
        },
        {
          title: 'Gradle Trademark Usage Guidelines',
          link: { href: '/legal/gradle-trademark-usage-guidelines/', external: false },
        },
      ],
    },
  ],
};
